import { Button } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PracticalInformationCard } from 'src/Components/PracticalInformation/PracticalInformationCard';
import { useDeletePracticalInformation } from 'src/Hooks/Mutations/useDeletePracticalInformation';
import useFetchAllPracticalInformation from 'src/Hooks/Queries/useFetchAllPracticalInformation';

import styles from './PracticalInformationList.module.scss';
import { Props } from './PracticalInformationList.types';

const PracticalInformationList = ({ selectedCategories, onResetFilters }: Props) => {
  const { data: allInformation } = useFetchAllPracticalInformation();
  const { mutate: deletePracticalInformation } = useDeletePracticalInformation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filteredInformation = useMemo(() => {
    if (selectedCategories.length === 0) {
      return allInformation ?? [];
    }
    const selectedSet = new Set(selectedCategories);
    return (allInformation ?? []).filter(information => selectedSet.has(information.type));
  }, [allInformation, selectedCategories]);

  const hasTotalItems = (allInformation?.length ?? 0) > 0;
  const isFilteredEmpty = hasTotalItems && filteredInformation.length === 0;

  if (isFilteredEmpty) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyMessage}>
          {t('practicalInformation.filters.emptyFiltered', 'Aucune fiche dans cette catégorie')}
        </div>
        <Button variant="light" onClick={onResetFilters}>
          {t('practicalInformation.filters.resetFilters', 'Voir toutes les catégories')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {filteredInformation.map(information => (
        <PracticalInformationCard
          id={information.id}
          key={information.id}
          type={information.type}
          subtype={information.subtype}
          title={information.title}
          fields={information.fields}
          createdAt={information.updatedAt}
          icon={information.icon}
          onDelete={() => deletePracticalInformation(information.id)}
          readonly={information.isSystem}
          onClick={() => navigate(information.id)}
          reminderTimeOfDay={information.reminderTimeOfDay}
          reminderFieldKey={information.reminderFieldKey}
        />
      ))}
    </div>
  );
};

export default PracticalInformationList;
